import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  Filter,
  AlertTriangle,
  TrendingUp,
  Shield,
  Eye,
  CheckCircle2,
  Calendar,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Tag
} from 'lucide-react';
import { TeamRiskService } from '@/services/team-risk.service';
import { Risk, RiskStatus, RiskSeverity, RiskLikelihood, RiskStatusLabels, RiskSeverityLabels, RiskLikelihoodLabels } from '@/models/team-risk.model';

export const RisksRegister: React.FC = () => {
  const navigate = useNavigate();
  const [risks, setRisks] = useState<Risk[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<RiskStatus | 'all'>('all');
  const [filterSeverity, setFilterSeverity] = useState<RiskSeverity | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRisks, setExpandedRisks] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'riskScore' | 'createdAt' | 'severity'>('riskScore');

  useEffect(() => {
    loadRisks();
  }, []);

  const loadRisks = async () => {
    try {
      setLoading(true);
      const allRisks = await TeamRiskService.getAllRisks();
      setRisks(allRisks);
    } catch (error) {
      console.error('Error loading risks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAndSortedRisks = () => {
    let filtered = risks.filter(risk => {
      const matchesSearch = searchTerm === '' ||
        risk.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        risk.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || risk.status === filterStatus;
      const matchesSeverity = filterSeverity === 'all' || risk.severity === filterSeverity;

      return matchesSearch && matchesStatus && matchesSeverity;
    });

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'riskScore') {
        return b.riskScore - a.riskScore;
      } else if (sortBy === 'createdAt') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      } else {
        const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        return severityOrder[b.severity] - severityOrder[a.severity];
      }
    });

    return filtered;
  };

  const getSeverityBadge = (severity: RiskSeverity) => {
    const badges = {
      [RiskSeverity.low]: 'bg-gray-100 text-gray-800',
      [RiskSeverity.medium]: 'bg-yellow-100 text-yellow-800',
      [RiskSeverity.high]: 'bg-orange-100 text-orange-800',
      [RiskSeverity.critical]: 'bg-red-100 text-red-800',
    };
    return badges[severity];
  };

  const getStatusBadge = (status: RiskStatus) => {
    const badges = {
      [RiskStatus.draft]: 'bg-gray-100 text-gray-800',
      [RiskStatus.identified]: 'bg-blue-100 text-blue-800',
      [RiskStatus.assessed]: 'bg-purple-100 text-purple-800',
      [RiskStatus.mitigating]: 'bg-yellow-100 text-yellow-800',
      [RiskStatus.monitoring]: 'bg-cyan-100 text-cyan-800',
      [RiskStatus.resolved]: 'bg-green-100 text-green-800',
      [RiskStatus.realized]: 'bg-red-100 text-red-800',
    };
    return badges[status];
  };

  const getRiskScoreColor = (score: number) => {
    if (score >= 15) return 'text-red-600';
    if (score >= 10) return 'text-orange-600';
    if (score >= 5) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const toggleExpanded = (riskId: string) => {
    const newExpanded = new Set(expandedRisks);
    if (newExpanded.has(riskId)) {
      newExpanded.delete(riskId);
    } else {
      newExpanded.add(riskId);
    }
    setExpandedRisks(newExpanded);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading risks...</p>
        </div>
      </div>
    );
  }

  const filteredRisks = getFilteredAndSortedRisks();

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/team-workspace')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <AlertTriangle className="w-8 h-8 mr-3 text-orange-600" />
              Risk Register
            </h1>
            <p className="text-gray-600 mt-1">Track and mitigate identified risks</p>
          </div>
        </div>
      </div>

      {/* Search, Filters, and Sort */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search risks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'riskScore' | 'createdAt' | 'severity')}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="riskScore">Sort by Risk Score</option>
            <option value="severity">Sort by Severity</option>
            <option value="createdAt">Sort by Date</option>
          </select>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Status:</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as RiskStatus | 'all')}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Statuses</option>
                  {Object.values(RiskStatus).map((status) => (
                    <option key={status} value={status}>
                      {RiskStatusLabels[status]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Severity:</label>
                <select
                  value={filterSeverity}
                  onChange={(e) => setFilterSeverity(e.target.value as RiskSeverity | 'all')}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Severities</option>
                  {Object.values(RiskSeverity).map((severity) => (
                    <option key={severity} value={severity}>
                      {RiskSeverityLabels[severity]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Risks List */}
      <div className="space-y-4">
        {filteredRisks.length === 0 ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
            <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No risks found</p>
            <p className="text-gray-500 text-sm mt-2">Upload a meeting to identify risks</p>
          </div>
        ) : (
          filteredRisks.map((risk) => {
            const isExpanded = expandedRisks.has(risk.id);

            return (
              <div
                key={risk.id}
                className="bg-white rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Risk Header */}
                      <div className="flex items-center space-x-3 mb-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getSeverityBadge(risk.severity)}`}>
                          {RiskSeverityLabels[risk.severity]}
                        </span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(risk.status)}`}>
                          {RiskStatusLabels[risk.status]}
                        </span>
                        <span className={`font-bold text-lg ${getRiskScoreColor(risk.riskScore)}`}>
                          Risk Score: {risk.riskScore}
                        </span>
                        {risk.containsPHI && (
                          <span className="px-2.5 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            PHI
                          </span>
                        )}
                      </div>

                      {/* Risk Title */}
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {risk.title}
                      </h3>

                      {/* Risk Description */}
                      <p className="text-gray-600 mb-4">
                        {risk.description}
                      </p>

                      {/* Risk Meta */}
                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Tag className="w-4 h-4 mr-1" />
                          {risk.category}
                        </div>
                        <div className="flex items-center">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          {RiskLikelihoodLabels[risk.likelihood]} Likelihood
                        </div>
                        {risk.estimatedCost && (
                          <div className="flex items-center">
                            <DollarSign className="w-4 h-4 mr-1" />
                            ${risk.estimatedCost.toLocaleString()} estimated impact
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => toggleExpanded(risk.id)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-600" />
                      )}
                    </button>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
                      {risk.potentialImpact && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Potential Impact</h4>
                          <p className="text-gray-600">{risk.potentialImpact}</p>
                        </div>
                      )}

                      {risk.affectedAreas && risk.affectedAreas.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Affected Areas</h4>
                          <div className="flex flex-wrap gap-2">
                            {risk.affectedAreas.map((area, idx) => (
                              <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                                {area}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {risk.mitigationStrategy && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Mitigation Strategy</h4>
                          <p className="text-gray-600">{risk.mitigationStrategy}</p>
                          {risk.mitigationOwner && (
                            <p className="text-sm text-gray-500 mt-2">
                              Owner: {risk.mitigationOwner}
                              {risk.mitigationDueDate && ` • Due: ${new Date(risk.mitigationDueDate).toLocaleDateString()}`}
                            </p>
                          )}
                        </div>
                      )}

                      {risk.mitigations && risk.mitigations.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Mitigation Actions</h4>
                          <div className="space-y-2">
                            {risk.mitigations.map((mitigation) => (
                              <div key={mitigation.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                                {mitigation.status === 'completed' ? (
                                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                ) : (
                                  <Eye className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="flex-1">
                                  <p className="text-gray-900">{mitigation.description}</p>
                                  <p className="text-sm text-gray-500 mt-1">
                                    {mitigation.responsibleName}
                                    {mitigation.dueDate && ` • Due: ${new Date(mitigation.dueDate).toLocaleDateString()}`}
                                  </p>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  mitigation.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  mitigation.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {mitigation.status.replace('_', ' ')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {risk.sourceTextSnippet && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Source Context</h4>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 italic">"{risk.sourceTextSnippet}"</p>
                            <p className="text-xs text-gray-500 mt-2">From: {risk.meetingTitle}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Total Risks</div>
          <div className="text-2xl font-bold text-gray-900">{risks.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Critical/High</div>
          <div className="text-2xl font-bold text-red-600">
            {risks.filter(r => r.severity === RiskSeverity.critical || r.severity === RiskSeverity.high).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Mitigating</div>
          <div className="text-2xl font-bold text-yellow-600">
            {risks.filter(r => r.status === RiskStatus.mitigating).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Resolved</div>
          <div className="text-2xl font-bold text-green-600">
            {risks.filter(r => r.status === RiskStatus.resolved).length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RisksRegister;
